/**
 * Internal Transaction Contracts
 *
 * DATABASE-FIRST: Schemas match PostgreSQL exactly
 * Source: MCP postgres queries on 2026-01-02
 *
 * Tables: deposits, withdrawals
 * Deposit status CHECK: pending, paid, completed, failed, cancelled
 * Withdrawal status CHECK: pending, paid, completed, declined
 *
 * Used by:
 * - payment-service → backend (create/update transactions, lookup by dealId)
 *
 * NOTE: These are legacy endpoints. New code should use:
 * - Deposits: POST /api/wallet/deposit (user-facing)
 * - Withdrawals: POST /api/wallet/withdrawal (user-facing)
 */
import { z } from 'zod';
import { bigintString } from '../common.js';
// =============================================================================
// TRANSACTION TYPE
// =============================================================================
export const transactionTypeValues = ['deposit', 'withdrawal'];
export const transactionTypeSchema = z.enum(transactionTypeValues);
// =============================================================================
// CREATE TRANSACTION
// Endpoint: POST /api/internal/transaction/create
// Legacy endpoint for payment-service to create deposits
// =============================================================================
/**
 * Create transaction request
 * Note: Only deposits are supported via this endpoint
 * Withdrawals should use /api/wallet/withdrawal
 */
export const createTransactionRequestSchema = z.object({
    telegramId: bigintString, // User telegram ID (as string for bigint)
    type: z.literal('deposit'), // Only deposits supported
    amount: z.number().positive(),
    paymentMethod: z.string().optional(),
    providerData: z.record(z.unknown()).optional(),
});
/**
 * Create transaction response
 */
export const createTransactionResponseSchema = z.object({
    success: z.boolean(),
    transactionId: z.string().optional(), // Legacy field (always '0')
    referenceNumber: z.string().optional(), // DEP-YYYYMMDD-NNNNN
    error: z.string().optional(),
});
// =============================================================================
// UPDATE TRANSACTION
// Endpoint: POST /api/internal/transaction/update
// Legacy endpoint for payment-service to update transaction status
// =============================================================================
/**
 * Transaction update status values
 * Note: These are internal processing states, not DB status values
 */
export const transactionUpdateStatusValues = ['pending', 'processing', 'completed', 'failed'];
export const transactionUpdateStatusSchema = z.enum(transactionUpdateStatusValues);
/**
 * Update transaction request
 * Can use either transactionId (legacy) or referenceNumber (preferred)
 */
export const updateTransactionRequestSchema = z.object({
    transactionId: z.string().optional(), // Legacy - may not work
    referenceNumber: z.string().optional(), // DEP-* or WTH-* or REF-*
    status: transactionUpdateStatusSchema.optional(),
    paymentMethod: z.string().optional(),
    providerData: z.record(z.unknown()).optional(),
    errorMessage: z.string().optional(),
});
/**
 * Update transaction response
 */
export const updateTransactionResponseSchema = z.object({
    success: z.boolean(),
    error: z.string().optional(),
});
// =============================================================================
// GET TRANSACTION BY DEAL ID
// Endpoint: GET /api/internal/transaction/:dealId
// Lookup transaction by provider's dealId (provider_transaction_id)
// =============================================================================
/**
 * Transaction data - unified view of deposit or withdrawal
 * Returned when looking up by provider dealId
 */
export const transactionDataSchema = z.object({
    id: z.string(), // Legacy field (always '0')
    telegramId: bigintString,
    type: transactionTypeSchema,
    amount: z.number(),
    status: z.string(), // Deposit or withdrawal status
    paymentMethod: z.string().optional(), // provider_hash
    providerData: z.object({
        referenceNumber: z.string(),
    }),
    createdAt: z.string(), // ISO datetime string
});
/**
 * Get transaction by deal ID response
 */
export const getTransactionByDealIdResponseSchema = z.object({
    success: z.boolean(),
    transaction: transactionDataSchema.optional(),
});
//# sourceMappingURL=transaction.js.map